import MaggieRounded from '@/assets/images/page-header/maggie-rounded.svg';
import GrantRounded from '@/assets/images/page-header/grant-rounded.svg';
import Image from 'next/image';
import Text from '@/components/basic/text';
import { useSessionContext } from 'supertokens-auth-react/recipe/session';
import {
  hasAdminRole,
  hasGrantManagerRole,
  hasSocialMediaManagerRole,
} from '@/lib/auth-service';
import { cn } from '@/lib/utils';
import { useOrganizationContext } from '@/contexts/organization-context';
import { useRouter } from 'next/router';
import { RoutePaths } from '@/consts/routes';
import { AvailableTeamMembersEnum } from '@/__generated__/graphql';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { env } from 'next-runtime-env';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuTrigger,
} from '@/components/shadcn/navigation-menu';
import { Separator } from '@radix-ui/react-dropdown-menu';
import { ListItem } from './nav-bar';
import { TicketType } from '@/types/hubspot-tickets';
import { logError } from '@/lib/rum';
import MemberRequestedModal from '../common/member-requested-modal';
import { useState } from 'react';
import { logInfo } from '@/lib/rum';
import { useLoadingTabs } from '@/hooks/use-loading-tabs';
import { WorkspaceNavigation } from './workspace-nav';

const WEBSITE_DOMAIN = env('WEBSITE');

export function AgentTabs() {
  const { organizationId, isGrantUser, isMaggieUser } =
    useOrganizationContext();
  const session = useSessionContext();
  const router = useRouter();
  const isMaggieDashboardEnabled =
    useFeatureFlagEnabled('maggie-dashboard') === true;
  const isGrantDashboardEnabled =
    useFeatureFlagEnabled('grant-dashboard') === true;
  const isGrantDashboardVeeEnabled =
    useFeatureFlagEnabled('grant-dashboard-orgs') === true;
  const [isOpen, setIsOpen] = useState(false);
  const [title, setTitle] = useState('');
  const { loadingTabs } = useLoadingTabs();

  if (session.loading || !session.doesSessionExist) {
    return null;
  }
  const isMaggieAvailable =
    hasSocialMediaManagerRole(session.accessTokenPayload['st-role']?.v) &&
    isMaggieUser &&
    isMaggieUser !== undefined;
  const isMaggieActive = location.pathname.includes(RoutePaths.MaggieHome);
  const isGrantAvailable =
    hasGrantManagerRole(session.accessTokenPayload['st-role']?.v) &&
    isGrantUser &&
    isGrantUser !== undefined;
  const isGrantActive = location.pathname.includes(RoutePaths.GrantHome);
  const isAdmin = hasAdminRole(session.accessTokenPayload['st-role']?.v);
  const ticketType = title === 'Grant' ? TicketType.GRANT : TicketType.MAGGIE;

  const handleNavigation = (title: string, url: string) => {
    const isActiveMember =
      title === AvailableTeamMembersEnum.Grant
        ? isGrantUser
        : title === AvailableTeamMembersEnum.Maggie
          ? isMaggieUser
          : false;
    if (isActiveMember) {
      const redirectUrl = `${url}?organizationId=${organizationId}`;

      if (
        title === AvailableTeamMembersEnum.Maggie &&
        !isMaggieDashboardEnabled
      ) {
        window.location.href = `${env('WEBSITE')}${RoutePaths.MaggieHome}/${RoutePaths.PostsClient}?orgId=${organizationId}`;
      } else if (
        title === AvailableTeamMembersEnum.Grant &&
        !isGrantDashboardEnabled &&
        !isGrantDashboardVeeEnabled
      ) {
        window.location.href = `${env('WEBSITE')}${RoutePaths.GrantHome}/${RoutePaths.Opportunities}?organizationId=${organizationId}`;
      } else {
        router.push(redirectUrl);
      }
    }
  };

  const handleTicketOpen = (title: string) => {
    if (isAdmin || loadingTabs) {
      return;
    }
    setTitle(title);
    fetch('/api/hubspot/ticket', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        organizationId,
        ticketType,
      }),
    })
      .then((res) => {
        if (res.status !== 200) {
          logError('Failed to create HubSpot ticket', { res });
        } else {
          logInfo(
            `Hubspot Ticket created for: ${ticketType} for organization ${organizationId}`,
            { res }
          );
        }
        setIsOpen(true);
      })
      .catch((error) => {
        logError('Failed to create HubSpot ticket', { error });
      });
  };

  return (
    <div className='flex w-full items-center justify-between gap-1 2xl:px-12'>
      <WorkspaceNavigation />
      {isAdmin ? (
        <NavigationMenu>
          <NavigationMenuItem
            className={cn('list-none', !organizationId && '-translate-y-2')}
          >
            <NavigationMenuTrigger
              onClick={(e) => {
                if (!isMaggieAvailable) {
                  e.preventDefault();

                  handleTicketOpen('Maggie');
                }
              }}
              className='!bg-transparent px-2'
              hideIcon={true}
              onPointerMove={(event) => event.preventDefault()}
              onPointerLeave={(event) => event.preventDefault()}
            >
              <div
                className={cn(
                  'flex w-[6.25rem] cursor-pointer items-center justify-center gap-1 rounded-t-lg border-b-2 border-b-transparent px-2.5 py-3 hover:border-b-maggie-400 hover:bg-maggie-50',
                  !isMaggieAvailable && 'pointer-events-none opacity-60',
                  isMaggieActive && 'border-b-maggie-400 bg-maggie-50'
                )}
              >
                <Image src={MaggieRounded} alt='maggie' />
                <Text weight='medium' variant='body2'>
                  Maggie
                </Text>
              </div>
            </NavigationMenuTrigger>
            <NavigationMenuContent
              onPointerEnter={(event) => event.preventDefault()}
              onPointerLeave={(event) => event.preventDefault()}
            >
              <ul className='flex flex-col'>
                <ListItem
                  href={`${WEBSITE_DOMAIN}/maggie/source-content-management/org/${organizationId}`}
                  title='Source Content Management'
                />
                <>
                  <Separator className='m-0 h-[1px] bg-muted' />
                  <ListItem
                    href={`${WEBSITE_DOMAIN}/maggie/posts-overview/org/${organizationId}`}
                    title='Posts Overview'
                  />
                </>
                <>
                  <Separator className='m-0 h-[1px] bg-muted' />
                  <ListItem
                    href={`${WEBSITE_DOMAIN}/maggie/events-calendar/org/${organizationId}`}
                    title='Organization Event Calendar'
                  />
                </>
                {hasSocialMediaManagerRole(
                  session.accessTokenPayload['st-role']?.v
                ) && (
                  <>
                    <Separator className='m-0 h-[1px] bg-muted' />
                    <ListItem
                      href={
                        isMaggieDashboardEnabled
                          ? `/maggie?organizationId=${organizationId}`
                          : `${WEBSITE_DOMAIN}/maggie/posts/org/${organizationId}`
                      }
                      title='Posts Approval'
                    />
                  </>
                )}
                {hasSocialMediaManagerRole(
                  session.accessTokenPayload['st-role']?.v
                ) && (
                  <>
                    <Separator className='m-0 h-[1px] bg-muted' />
                    <ListItem
                      href={`${WEBSITE_DOMAIN}/maggie/social-calendar-management/org/${organizationId}`}
                      title='Social Calendar'
                    />
                  </>
                )}
                <>
                  <Separator className='m-0 h-[1px] bg-muted' />
                  <ListItem
                    href={`${WEBSITE_DOMAIN}/maggie/writing-preferences?organizationId=${organizationId}`}
                    title='Writing Preferences'
                  />
                </>
              </ul>
            </NavigationMenuContent>
          </NavigationMenuItem>
        </NavigationMenu>
      ) : (
        <div
          onClick={() => {
            if (!isMaggieAvailable && isMaggieUser !== undefined) {
              handleTicketOpen('Maggie');
            } else {
              handleNavigation(
                AvailableTeamMembersEnum.Maggie,
                RoutePaths.MaggieHome
              );
            }
          }}
          className={cn(
            'flex w-[6.25rem] cursor-pointer items-center justify-center gap-1 rounded-t-lg border-b-2 border-b-transparent px-2.5 py-3 hover:border-b-maggie-400 hover:bg-maggie-50',
            !isMaggieAvailable && 'pointer-events-none opacity-60',
            isMaggieActive && 'border-b-maggie-400 bg-maggie-50'
          )}
        >
          <Image src={MaggieRounded} alt='maggie' />
          <Text weight='medium' variant='body2'>
            Maggie
          </Text>
        </div>
      )}

      {isAdmin ? (
        <NavigationMenu>
          <NavigationMenuItem
            className={cn('list-none', !organizationId && '-translate-y-2')}
          >
            <NavigationMenuTrigger
              onClick={(e) => {
                if (!isGrantAvailable) {
                  e.preventDefault();

                  handleTicketOpen('Grant');
                }
              }}
              className='!bg-transparent px-2'
              hideIcon={true}
              onPointerMove={(event) => event.preventDefault()}
              onPointerLeave={(event) => event.preventDefault()}
            >
              <div
                className={cn(
                  'flex w-[6.25rem] cursor-pointer items-center justify-center gap-1 rounded-t-lg border-b-2 border-b-transparent px-2.5 py-3 hover:border-b-grant-400 hover:bg-grant-50',
                  !isGrantAvailable && 'pointer-events-none opacity-60',
                  isGrantActive && 'border-b-grant-400 bg-grant-50'
                )}
              >
                <Image src={GrantRounded} alt='grant' />
                <Text weight='medium' variant='body2'>
                  Grant
                </Text>
              </div>
            </NavigationMenuTrigger>
            <NavigationMenuContent
              onPointerEnter={(event) => event.preventDefault()}
              onPointerLeave={(event) => event.preventDefault()}
            >
              <ul className='flex flex-col'>
                <ListItem
                  href={
                    isGrantDashboardEnabled || isGrantDashboardVeeEnabled
                      ? `/grant?organizationId=${organizationId}&tab=work-plan`
                      : `/grant/opportunities?organizationId=${organizationId}`
                  }
                  title='Opportunities'
                />
                <ListItem
                  href={`/grant/opportunities/admin?organizationId=${organizationId}`}
                  title='Manage Opportunities'
                />
                <ListItem
                  href={`/grant/grants?organizationId=${organizationId}`}
                  title='Grants DB operations'
                />
              </ul>
            </NavigationMenuContent>
          </NavigationMenuItem>
        </NavigationMenu>
      ) : (
        <div
          onClick={() => {
            if (!isGrantAvailable && isGrantUser !== undefined) {
              handleTicketOpen('Grant');
            } else {
              handleNavigation(
                AvailableTeamMembersEnum.Grant,
                RoutePaths.GrantHome
              );
            }
          }}
          className={cn(
            'flex w-[6.25rem] cursor-pointer items-center justify-center gap-1 rounded-t-lg border-b-2 border-b-transparent px-2.5 py-3 hover:border-b-grant-400 hover:bg-grant-50',
            !isGrantAvailable && 'pointer-events-none opacity-60',
            isGrantActive && 'border-b-grant-400 bg-grant-50'
          )}
        >
          <Image src={GrantRounded} alt='grant' />
          <Text weight='medium' variant='body2'>
            Grant
          </Text>
        </div>
      )}
      <MemberRequestedModal
        title={title}
        isOpen={isOpen}
        onOpenChange={setIsOpen}
      />
    </div>
  );
}
